import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import "./DataGrid.scss";
import "./SearchInput.scss";

import orderBy from "lodash/orderBy";
import DataTable from "react-data-table-component";
import { Box, Grid } from "@mui/material";
import { IconButton, InputBase, Paper } from "@mui/material";
import TooltipText from "@mui/material/Tooltip";

import { Delete, Help, InsertDriveFile } from "@mui/icons-material/";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import Search from "@mui/icons-material/Search";

import Toaster from "./Toaster";
import { CommonUtil } from "../util/Utils";
import DeleteDialog from "./DeleteDialog";
import { useAppDispatch } from "../store/hooks";

import PlagiarismTwoToneIcon from "@mui/icons-material/PlagiarismTwoTone";

import { DateTimeUtil } from "../util/DateTimeUtils";
import GlobalLoader from "./GlobalLoader";

const customStyles: any = {
  headCells: {
    style: {
      fontSize: "12px",
      fontWeight: "500",
      backgroundColor: "inherit",
      color: "white",
    },
  },
  cells: {
    style: {
      backgroundColor: "inherit",
      borderBottom: "1px solid rgba(0,0,0,.12)",
    },
  },
  rows: {
    style: {
      "&:not(:last-of-type)": {
        borderBottom: "none",
      },
    },
  },
  headRow: {
    style: {
      backgroundColor: "#4d4f5c",
      borderRadius: "15px 15px 0px 0px",
      borderBottomWidth: "0px",
    },
  },
};

const conditionalRowStyles = [
  {
    when: (row: any) => row?.studentStatus === "Discontinued",
    style: {
      backgroundColor: "#f6d9d9", // Light red
    },
  },
];

interface DataGridProps {
  columns: any;
  data: any;
  action?: any;
  searchParam?: any;
  setUserActionPerform?: any;
  canMultipleDelete?: boolean;
  onDeleteSuccess?: any;
  setSelectedTableRows?: any;
  selectableRowsHighlight?: boolean;
  highlightOnHover?: boolean;
  selectedRows?: any;
  onSelectedTableRowsChange?: any;
  canExport?: boolean;
  fixedHeader?: boolean;
  fixedHeaderScrollHeight?: string;
  haveHelpManual?: boolean;
  selectableRows?: boolean;
  defaultSortFieldId?: number;
  paginationPerPage?: number;
  paginationRowsPerPageOptions?: any;
  expandableRows?: boolean;
  onRowExpandToggled?: any;
  expandableRowExpanded?: any;
  expandableRowsComponent?: any;
  expandableRowsComponentProps?: any;
  sortingDirection?: any;
  callOnExport?: any;
  exportData?: any;
}

const DataGrid = (props: DataGridProps) => {
  const { data, action, searchParam, setUserActionPerform } = props;
  const { canMultipleDelete, onDeleteSuccess, canExport } = props;
  const {
    setSelectedTableRows,
    onSelectedTableRowsChange,
    selectedRows,
    sortingDirection,
    selectableRowsHighlight,
    highlightOnHover,
  } = props;
  const { haveHelpManual, selectableRows, defaultSortFieldId } = props;
  const { paginationPerPage, paginationRowsPerPageOptions } = props;
  const { callOnExport, exportData } = props;
  const [isSelectIonCleared, setIsSelectIonCleared] = useState(false);

  const dateFormat = process.env.REACT_APP_DEFAULT_DATE_FORMAT;

  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [selectedRow, setSelectedRow] = useState<any>([]);
  const [canDelete, setCanDelete] = useState(undefined);

  const [resetPaginationToggle, setResetPaginationToggle] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [filterText, setFilterText] = useState("");
  const [spinner, setSpinner] = useState<boolean>(false);

  const paginationComponentOptions = {
    selectAllRowsItem: true,
    selectAllRowsItemText: "ALL",
  };

  useEffect(() => {
    setIsSelectIonCleared(false);
    setSelectedRow([]);
  }, [data]);

  useEffect(() => {
    selectedRows && selectedRows.length === 0 && setIsSelectIonCleared(false);
    selectedRows &&
      selectedRows.length === 0 &&
      setIsSelectIonCleared((pre) => !pre);
  }, [selectedRows]);

  const filteredItems =
    data &&
    data.filter((item: any) => {
      if (data.length > 0 && searchParam && searchParam.length > 0) {
        return searchParam.some((columnName: string) => {
          return (
            item &&
            columnName &&
            item[columnName] &&
            item[columnName]
              .toString()
              .toLowerCase()
              .includes(filterText.toLowerCase())
          );
        });
      } else {
        return data;
      }
    });

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
  };

  const handleDeleteModalOpen = () => {
    setDeleteDialogOpen(true);
  };

  const handleHelpModalOpen = () => {
    // Remove console once method is implemented
    console.log("HelpModalOpen");
  };

  // Handle Multi Select
  const handleOnSelectedRowsChange = ({ selectedRows }: any) => {
    setSelectedRow(selectedRows);
    highlightOnHover &&
      selectableRowsHighlight &&
      setSelectedTableRows &&
      setSelectedTableRows(selectedRows);
    if (onSelectedTableRowsChange) {
      onSelectedTableRowsChange(selectedRows);
    }
    setCanDelete(
      selectedRows.every(
        (item: any) => item?.canDelete === true || item?.canDelete === undefined
      )
    );
  };

  // Multi Delete functionality
  const deleteSelectedGridItems = () => {
    let ids = "";
    selectedRow.forEach((item: any, index: number) => {
      if (index === 0) {
        ids += `ids=${item.id}`;
      } else {
        ids += `&ids=${item.id}`;
      }
    });

    dispatch(
      action({
        requestParams: ids,
        apiAction: "deleteAll",
      })
    )
      .then((result: any) => {
        if (result?.payload?.status === 200) {
          setSelectedRow([]);
          Toaster.success(undefined, result.payload.data.message);

          handleCloseDeleteDialog();
          if (setUserActionPerform) {
            setUserActionPerform((prev: boolean) => !prev);
          }
          if (onDeleteSuccess) {
            onDeleteSuccess();
            setIsSelectIonCleared(false);
          }
        } else {
          Toaster.error(undefined, result.payload);
        }
      })
      .catch((err: any) => {
        //This console for debug the app if any error occurred in DEV
        console.log(err);
      });
  };

  // Export excel functionality
  const exportGridData = () => {
    setSpinner(true);
    dispatch(
      action({
        formValues: selectedRow.map((item: any) => item.id),
        apiAction: "exportExcel",
      })
    )
      .then((result: any) => {
        if (result?.payload?.status === 200) {
          CommonUtil.download(result?.payload);
          callOnExport && callOnExport(result?.payload);
        } else {
          Toaster.error(undefined, result.payload);
        }
      })
      .catch((err: any) => {
        //This console for debug the app if any error occurred in DEV
        console.log(err);
      })
      .finally(() => {
        setSpinner(false);
      });
  };

  //Global sorting
  const handleCustomSort = (rows: any, field: any, direction: any) => {
    setIsSelectIonCleared(true);

    let fieldName = rows.length ? findObjectKeyIndex(rows, field) : "";

    if (fieldName === "id") {
      return rows.sort(function compare(row1: any, row2: any): number {
        return row2.id - row1.id;
      });
    } else if (fieldName.toLowerCase().includes("date")) {
      if (fieldName === "invoiceMonths") {
        fieldName = "forDate";
      }
      return rows.sort(function compare(row1: any, row2: any): number {
        // nulls sort after anything else
        if (row1[fieldName] === null) return direction === "desc" ? -1 : 1;
        if (row2[fieldName] === null) return direction === "desc" ? 1 : -1;
        let date1: Date = DateTimeUtil.format(
          row1[fieldName],
          dateFormat,
          true
        ) as Date;
        let date2: Date = DateTimeUtil.format(
          row2[fieldName],
          dateFormat,
          true
        ) as Date;
        return direction === "desc"
          ? date2.valueOf() - date1.valueOf()
          : date1.valueOf() - date2.valueOf();
      });
    } else {
      const handleField = (row: any) => {
        if (row[fieldName] && typeof row[fieldName] === "string") {
          return String(row[fieldName])?.toLowerCase();
        }
        return row[fieldName];
      };
      /* This is kept for future reference */
      // return orderBy(rows, handleField, direction);
      return orderBy(
        rows,
        handleField,
        sortingDirection ? sortingDirection : direction
      );
    }
  };

  const findObjectKeyIndex = (rows: any, field: any) => {
    let columnName: string = "";
    Object.keys(rows[0])?.forEach((item, index: number) => {
      if (new RegExp("\\b" + item + "\\b")?.test(String(field))) {
        columnName = Object.keys(rows[0])[index];
      }
    });
    return columnName;
  };

  return (
    <>
      <Grid
        container
        spacing={1}
        columnSpacing={{ xs: 1, sm: 1, md: 1, lg: 1 }}
        my="20px"
      >
        {searchParam && searchParam.length > 0 && (
          <Grid item xs={12} sm={6} md={6} lg={4}>
            <SearchInput
              onFilter={(e: any) => {
                e.preventDefault();
                setFilterText(e.target.value);
              }}
              filterText={filterText}
            />
          </Grid>
        )}

        <Grid item xs={12} sm={6} md={6} lg={2} className="grid-action">
          <div style={{ display: "flex" }}>
            {haveHelpManual && (
              <div className="center">
                <div
                  className={
                    canMultipleDelete || canExport ? "border-right" : ""
                  }
                >
                  <button onClick={handleHelpModalOpen}>
                    <Help />
                  </button>
                </div>
              </div>
            )}

            {canMultipleDelete && (
              <div className="center">
                <div
                  className={
                    "border-left" + (canExport ? " border-left-right" : "")
                  }
                >
                  <TooltipText title="Delete All" placement="top">
                    <button
                      disabled={!canDelete || selectedRow.length < 1}
                      onClick={handleDeleteModalOpen}
                    >
                      <Delete
                        className={
                          !canDelete || selectedRow.length < 1
                            ? "disabled"
                            : "delete"
                        }
                      />
                    </button>
                  </TooltipText>
                </div>
              </div>
            )}
            {canExport && (
              <div className="center">
                <div
                  className={
                    searchParam &&
                    searchParam.length > 0 &&
                    canExport &&
                    !canMultipleDelete
                      ? " border-left"
                      : ""
                  }
                >
                  <TooltipText title="Export All" placement="top">
                    <button
                      disabled={!canExport || selectedRow.length < 1}
                      onClick={exportData ? exportData : exportGridData}
                    >
                      <InsertDriveFile
                        className={
                          !canExport || selectedRow.length < 1
                            ? "disabled"
                            : "insert-drive"
                        }
                      />
                    </button>
                  </TooltipText>
                </div>
              </div>
            )}
          </div>
        </Grid>
      </Grid>

      <Box>
        <DataTable
          {...props}
          pagination
          striped
          fixedHeader
          fixedHeaderScrollHeight="80vh"
          sortFunction={handleCustomSort}
          paginationComponentOptions={{
            rowsPerPageText: `${t("rowsPerPage")}`,
            rangeSeparatorText: `${t("of")}`,
            noRowsPerPage: false,
            selectAllRowsItem: true,
            selectAllRowsItemText: `${t("all")}`,
          }}
          paginationPerPage={paginationPerPage ? paginationPerPage : 50}
          paginationRowsPerPageOptions={
            paginationRowsPerPageOptions
              ? paginationRowsPerPageOptions
              : [10, 20, 30, 40, 50, 100, 500]
          }
          selectableRows={selectableRows === false ? selectableRows : true}
          customStyles={customStyles}
          sortIcon={
            <div className="flex flex-columns">
              <ArrowDropDownIcon className="sort-icon" />
              <ArrowDropUpIcon className="sort-icon" />
            </div>
          }
          noDataComponent={
            <div>
              <div className="center">
                <PlagiarismTwoToneIcon
                  className="color-sec"
                  style={{ fontSize: "40px" }}
                ></PlagiarismTwoToneIcon>
              </div>
              <span className="small-f label-gray">{t("noResultsFound")}</span>
            </div>
          }
          data={filteredItems}
          paginationResetDefaultPage={resetPaginationToggle} // optionally, a hook to reset pagination to page 1
          persistTableHead
          onSelectedRowsChange={handleOnSelectedRowsChange}
          defaultSortFieldId={defaultSortFieldId ? defaultSortFieldId : 1}
          defaultSortAsc={false}
          // selectableRowsVisibleOnly={true}   /* This is kept for future reference */
          clearSelectedRows={isSelectIonCleared}
          conditionalRowStyles={conditionalRowStyles}
        />
        <GlobalLoader loading={spinner} />
      </Box>

      {/* Multi delete modal */}
      {/* delete modal */}
      <DeleteDialog
        isOpen={deleteDialogOpen}
        onClickCloseOrCancel={handleCloseDeleteDialog}
        onClickDelete={deleteSelectedGridItems}
      />
    </>
  );
};

export default DataGrid;

interface SearchInputProps {
  onFilter: any;
  filterText: string;
}

const SearchInput = ({ onFilter, filterText }: SearchInputProps) => {
  const { t } = useTranslation();

  return (
    <Paper className="search-input center">
      <IconButton type="button" aria-label="search" style={{ padding: "0" }}>
        <Search style={{ fontSize: "20px" }} />
      </IconButton>

      <InputBase
        id="search"
        aria-label="Search Input"
        sx={{ ml: 1, flex: 1 }}
        placeholder={t("Search")}
        style={{ paddingLeft: "0", marginLeft: "0" }}
        onChange={onFilter}
        value={filterText}
      />
    </Paper>
  );
};
